/**
 * Empty-state Apply layout for equipment tyre/battery schematics (W3.2 API).
 * SEARCH: reuse POST /pmv/components/equipment/:id/layout/apply — no parallel layout path.
 */

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Form, Modal, Radio, Select, Space, Typography, message } from 'antd';
import pmvService from '@/services/pmv.service';
import { useAuthStore } from '@/store/authStore';

const { Text } = Typography;

function canApplyLayout(perms: string[] | undefined, isSuperAdmin?: boolean): boolean {
  if (isSuperAdmin) return true;
  const p = perms || [];
  return (
    p.includes('pmv.component.manage') ||
    p.includes('pmv.component.configure') ||
    p.includes('pmv.admin') ||
    p.includes('pmv.manage')
  );
}

export const PmvApplyLayoutEmpty: React.FC<{
  profileId: string;
  description: string;
  onApplied?: () => void;
}> = ({ profileId, description, onApplied }) => {
  const user = useAuthStore((s) => s.user);
  const canManage = canApplyLayout(user?.permissions, user?.is_super_admin);
  const [open, setOpen] = useState(false);
  // Default to preset — classification link is usually empty on first use.
  const [mode, setMode] = useState<'from_classification' | 'template' | 'preset_mixed'>('preset_mixed');
  const [templateId, setTemplateId] = useState<string | undefined>();

  const templatesQ = useQuery({
    queryKey: ['pmv-position-templates'],
    queryFn: () => pmvService.listPositionTemplates(),
    enabled: open && canManage,
    staleTime: 30_000,
  });

  const metaQ = useQuery({
    queryKey: ['pmv-component-meta'],
    queryFn: () => pmvService.getComponentMeta(),
    enabled: open && canManage,
    staleTime: 60_000,
  });

  const templateOptions = useMemo(
    () =>
      (templatesQ.data || []).map((t) => ({
        value: t.id,
        label: `${t.code} — ${t.name} (${t.family_scope})`,
      })),
    [templatesQ.data]
  );

  const applyMut = useMutation({
    mutationFn: async () => {
      if (mode === 'from_classification') {
        return pmvService.applyEquipmentLayout(profileId, { from_classification: true });
      }
      if (mode === 'template') {
        if (!templateId) throw new Error('Select a position template');
        return pmvService.applyEquipmentLayout(profileId, { template_id: templateId });
      }
      const meta = metaQ.data || (await pmvService.getComponentMeta());
      const tyre = meta.presets?.tyre_4x2 || [];
      const bat = meta.presets?.battery_dual || [];
      if (!tyre.length && !bat.length) {
        throw new Error('Preset slots unavailable — refresh and try again');
      }
      const listed = templatesQ.data || (await pmvService.listPositionTemplates());
      let template = listed.find((t) => t.code === 'PRESET-4X2-DUALBAT');
      if (!template) {
        try {
          template = await pmvService.createPositionTemplate({
            code: 'PRESET-4X2-DUALBAT',
            name: '4x2 tyre + dual battery',
            family_scope: 'MIXED',
            description: 'Created from schematic empty-state preset',
            slots: [...tyre, ...bat],
          });
        } catch (createErr: unknown) {
          const msg = String(
            (createErr as { response?: { data?: { message?: string } }; message?: string })?.response
              ?.data?.message ||
              (createErr as Error)?.message ||
              ''
          );
          if (/already exists/i.test(msg)) {
            const again = await pmvService.listPositionTemplates();
            template = again.find((t) => t.code === 'PRESET-4X2-DUALBAT');
          }
          if (!template) throw createErr;
        }
      }
      return pmvService.applyEquipmentLayout(profileId, { template_id: template.id });
    },
    onSuccess: () => {
      message.success('Position layout applied');
      setOpen(false);
      onApplied?.();
    },
    onError: (e: unknown) => {
      const ax = e as {
        message?: string;
        response?: { data?: { message?: string } };
      };
      const msg = ax.response?.data?.message || ax.message || 'Could not apply layout';
      message.error(msg);
    },
  });

  return (
    <>
      <Empty
        description={
          <Space direction="vertical" size="small" style={{ maxWidth: 420 }}>
            <Text>{description}</Text>
            {!canManage ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ask a PMV manager to apply a position template for this equipment.
              </Text>
            ) : null}
          </Space>
        }
      >
        {canManage ? (
          <Button type="primary" onClick={() => setOpen(true)}>
            Apply layout
          </Button>
        ) : null}
      </Empty>

      <Modal
        title="Apply position layout"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => applyMut.mutate()}
        okText="Apply"
        confirmLoading={applyMut.isPending}
        destroyOnClose
        width={480}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Creates the wheel/battery slot map for this equipment"
          description="Does not install components. You can fit tyres and batteries after positions exist."
        />
        <Form layout="vertical">
          <Form.Item label="How to apply">
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <Radio value="preset_mixed">Create &amp; apply preset (4×2 tyre + dual battery)</Radio>
              <Radio value="template">Choose an existing template</Radio>
              <Radio value="from_classification">From equipment classification (linked template)</Radio>
            </Radio.Group>
          </Form.Item>
          {mode === 'template' ? (
            <Form.Item label="Position template" required>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={templatesQ.isLoading ? 'Loading…' : 'Select template'}
                options={templateOptions}
                value={templateId}
                onChange={setTemplateId}
                notFoundContent={
                  templatesQ.isLoading ? 'Loading…' : 'No templates yet — use the preset option'
                }
              />
            </Form.Item>
          ) : null}
          {mode === 'from_classification' ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message="Only works if a template is already linked to this classification"
              description="If Apply fails, switch to the preset option."
            />
          ) : null}
          {mode === 'preset_mixed' ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Recommended for first-time setup: creates FL/FR/RL/RR + spare and BAT-A/BAT-B positions.
            </Text>
          ) : null}
        </Form>
      </Modal>
    </>
  );
};

export default PmvApplyLayoutEmpty;
